import { afterNextRender, effect, inject, Injector, signal, type Signal } from '@angular/core';
import type { ManualLocation } from './manual-help.service';

export interface SectionScroll {
  /** Target waiting for its chapter to render; `null` when there is none. */
  readonly pending: Signal<ManualLocation | null>;
  /**
   * Sets the target. A location without a section clears any previous one, unless the helper was
   * created with `topWithoutSection`: then it scrolls the root to the top.
   */
  scrollTo(location: ManualLocation): void;
}

/**
 * Brings the section of a location into view once ITS chapter has rendered. The chapter id travels
 * with the target because opening a chapter is async (a router navigation, a query): until it
 * lands, the rendered chapter is still the previous one and the section does not exist yet. The
 * element is found by id (the section id is the DOM id) INSIDE the root its host gives: the help
 * panel and the `/manual` page can render the same chapter at once, so the id exists twice in the
 * document. `scrollIntoView` scrolls the nearest scrollable ancestor: the panel's own container,
 * or the document on the `/manual` page. Call it in an injection context.
 *
 * @param renderedChapterId Id of the chapter currently rendered, `null` while none is.
 * @param root Element of the host that holds the rendered chapter, `null` while it is not there.
 * @param options `topWithoutSection`: a persistent scroll container (the panel) keeps its offset
 *   between locations, so a location with no section must reset it.
 */
export function injectSectionScroll(
  renderedChapterId: () => string | null,
  root: () => HTMLElement | null,
  options: { topWithoutSection?: boolean } = {},
): SectionScroll {
  const injector = inject(Injector);
  const pending = signal<ManualLocation | null>(null);

  effect(() => {
    const target = pending();
    const container = root();
    if (!target || !container || renderedChapterId() !== target.chapterId) return;

    afterNextRender(
      () => {
        if (target.sectionId) {
          const id = target.sectionId.replace(/["\\]/g, '\\$&');
          container.querySelector(`[id="${id}"]`)?.scrollIntoView({ behavior: 'smooth' });
        } else {
          container.scrollTo({ top: 0 });
        }
        // A newer target may have replaced this one while the render was pending.
        if (pending() === target) pending.set(null);
      },
      { injector },
    );
  });

  return {
    pending: pending.asReadonly(),
    scrollTo: (location) =>
      pending.set(location.sectionId || options.topWithoutSection ? location : null),
  };
}
